import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';
import {
  TENANT_BUCKET,
  SIGNED_URL_TTL,
  FRONTEND_CACHE_TTL_MS,
  FRONTEND_CACHE_MAX_ENTRIES,
  FRONTEND_CACHE_EVICT_COUNT,
  extractStoragePath,
} from '@/lib/storage-config';

export interface MediaDownloadResult {
  success: boolean;
  fileUrl?: string;
  thumbnailUrl?: string;
  error?: string;
}

interface SignedCacheEntry {
  url: string;
  expiresAt: number;
  insertedAt: number;
}

class WhatsAppMediaService {
  private static instance: WhatsAppMediaService;
  private cache = new Map<string, string>(); // download dedup cache (by media ID)
  private signedCache = new Map<string, SignedCacheEntry>(); // TTL cache for signed URLs
  private downloadInProgress = new Map<string, Promise<MediaDownloadResult>>();
  private developmentMode = typeof window !== 'undefined' && typeof __DEV__ !== 'undefined' && __DEV__;

  static getInstance(): WhatsAppMediaService {
    if (!WhatsAppMediaService.instance) {
      WhatsAppMediaService.instance = new WhatsAppMediaService();
    }
    return WhatsAppMediaService.instance;
  }

  // ============================================================================
  // SIGNED URL RESOLUTION (new — used by useSignedMediaUrl hook and components)
  // ============================================================================

  /**
   * Resolve a media URL or storage path to a signed URL.
   * Uses TTL cache (50 min). External URLs pass through unchanged.
   */
  async resolveMediaUrl(urlOrPath: string): Promise<string | null> {
    if (!urlOrPath) return null;

    const extracted = extractStoragePath(urlOrPath);
    if (extracted === null) return urlOrPath; // external URL — pass through

    // Check TTL cache
    const cached = this.getSignedCached(extracted);
    if (cached) return cached;

    // Sign
    const signed = await this.signStoragePath(extracted);
    if (signed) {
      this.setSignedCache(extracted, signed);
    }
    return signed;
  }

  /**
   * Get a fresh signed URL (bypass cache). Use for zoom/download after long sessions.
   */
  async getFreshSignedUrl(urlOrPath: string): Promise<string | null> {
    if (!urlOrPath) return null;

    const extracted = extractStoragePath(urlOrPath);
    if (extracted === null) return urlOrPath; // external URL

    const signed = await this.signStoragePath(extracted);
    if (signed) {
      this.setSignedCache(extracted, signed);
    }
    return signed;
  }

  /**
   * Batch-resolve multiple media URLs/paths. Pre-populates the signed cache.
   * Call from message list level before individual components render.
   */
  async batchResolve(urlsOrPaths: (string | null | undefined)[]): Promise<void> {
    const toSign: { key: string; path: string }[] = [];

    for (const urlOrPath of urlsOrPaths) {
      if (!urlOrPath) continue;
      const extracted = extractStoragePath(urlOrPath);
      if (extracted === null) continue; // external
      if (this.getSignedCached(extracted)) continue; // already cached
      toSign.push({ key: extracted, path: '' }); // resolved below
    }

    if (toSign.length === 0) return;

    // Resolve full paths (prepend tenant ID for relative paths)
    for (const entry of toSign) {
      entry.path = await this.resolveFullPath(entry.key);
    }

    // Chunk at 100
    const CHUNK_SIZE = 100;
    for (let i = 0; i < toSign.length; i += CHUNK_SIZE) {
      const chunk = toSign.slice(i, i + CHUNK_SIZE);
      const { data, error } = await supabase.storage
        .from(TENANT_BUCKET)
        .createSignedUrls(chunk.map(e => e.path), SIGNED_URL_TTL.FRONTEND);

      if (error || !data) {
        if (import.meta.env.DEV) console.warn('[MediaService] Batch sign failed:', error?.message);
        continue;
      }

      for (let j = 0; j < chunk.length; j++) {
        if (data[j]?.signedUrl) {
          this.setSignedCache(chunk[j].key, data[j].signedUrl);
        }
      }
    }
  }

  // ============================================================================
  // SIGNED URL CACHE (LRU with TTL)
  // ============================================================================

  private getSignedCached(key: string): string | null {
    const entry = this.signedCache.get(key);
    if (!entry) return null;
    if (Date.now() >= entry.expiresAt) {
      this.signedCache.delete(key);
      return null;
    }
    entry.insertedAt = Date.now(); // LRU touch
    return entry.url;
  }

  private setSignedCache(key: string, url: string): void {
    // LRU eviction
    if (this.signedCache.size >= FRONTEND_CACHE_MAX_ENTRIES) {
      const entries = [...this.signedCache.entries()]
        .sort((a, b) => a[1].insertedAt - b[1].insertedAt);
      for (let i = 0; i < FRONTEND_CACHE_EVICT_COUNT && i < entries.length; i++) {
        this.signedCache.delete(entries[i][0]);
      }
    }
    this.signedCache.set(key, {
      url,
      expiresAt: Date.now() + FRONTEND_CACHE_TTL_MS,
      insertedAt: Date.now(),
    });
  }

  /** Resolve relative path to full storage path by prepending tenant ID */
  private async resolveFullPath(path: string): Promise<string> {
    // Already a full {tid}/... path — pass through unchanged
    if (/^[0-9a-f]{8}-/.test(path)) return path;
    const { data: { user } } = await supabase.auth.getUser();
    const tenantId = user?.user_metadata?.tenant_id;
    if (!tenantId) return path;
    if (path.startsWith('_platform/')) return `${tenantId}/${path}`;
    return `${tenantId}/custom-storage/${path}`;
  }

  private async signStoragePath(path: string): Promise<string | null> {
    const fullPath = await this.resolveFullPath(path);
    const { data, error } = await supabase.storage
      .from(TENANT_BUCKET)
      .createSignedUrl(fullPath, SIGNED_URL_TTL.FRONTEND);

    if (error || !data?.signedUrl) {
      if (import.meta.env.DEV) console.warn('[MediaService] Sign failed:', fullPath, error?.message);
      return null;
    }
    return data.signedUrl;
  }

  // ============================================================================
  // DOWNLOAD + RESOLVE (existing — used by MediaGif, MediaSticker, fallback path)
  // ============================================================================

  async downloadAndGetMediaUrl(
    tenantId: string,
    whatsappMediaId: string,
    mediaType: string,
    messageId?: string
  ): Promise<MediaDownloadResult> {
    const cacheKey = `${tenantId}-${whatsappMediaId}`;

    if (this.downloadInProgress.has(cacheKey)) {
      return await this.downloadInProgress.get(cacheKey)!;
    }

    const downloadPromise = this.performDownload(tenantId, whatsappMediaId, mediaType, messageId);
    this.downloadInProgress.set(cacheKey, downloadPromise);

    try {
      const result = await downloadPromise;
      return result;
    } finally {
      this.downloadInProgress.delete(cacheKey);
    }
  }

  private async performDownload(
    tenantId: string,
    whatsappMediaId: string,
    mediaType: string,
    messageId?: string
  ): Promise<MediaDownloadResult> {
    try {
      const cacheKey = `${tenantId}-${whatsappMediaId}`;

      if (this.cache.has(cacheKey)) {
        const cachedUrl = this.cache.get(cacheKey)!;
        return { success: true, fileUrl: cachedUrl };
      }

      // Check if already downloaded
      const { data: existingFile, error: dbError } = await supabase
        .from('media_files')
        .select('file_path, thumbnail_path, storage_bucket')
        .eq('tenant_id', tenantId)
        .eq('whatsapp_media_id', whatsappMediaId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dbError) {
        console.error('❌ WhatsAppMediaService: Database query error:', dbError);
      }

      if (existingFile) {
        const bucket = existingFile.storage_bucket || 'tenants';
        const fileUrl = await this.getSignedUrl(existingFile.file_path, bucket);
        const thumbnailUrl = existingFile.thumbnail_path
          ? await this.getSignedUrl(existingFile.thumbnail_path, bucket)
          : undefined;

        if (fileUrl) {
          this.cache.set(cacheKey, fileUrl);
          return { success: true, fileUrl, thumbnailUrl };
        }
      }

      // Download via edge function
      const { data, error } = await supabase.functions.invoke('download-whatsapp-media', {
        body: { tenantId, messageId, whatsappMediaId, mediaType }
      });

      if (error || !data?.success) {
        console.error('❌ WhatsAppMediaService: Edge function failed:', { error, data });
        throw new Error(data?.error || error?.message || 'Failed to download media');
      }

      // Edge function now returns signed URL directly as publicUrl
      const fileUrl = data.publicUrl || null;
      const thumbnailUrl = data.thumbnailUrl || undefined;

      if (fileUrl) {
        this.cache.set(cacheKey, fileUrl);
        return { success: true, fileUrl, thumbnailUrl };
      }

      throw new Error('Failed to get signed URL');

    } catch (error) {
      console.error('❌ WhatsAppMediaService: Download error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : i18n.t('common:unknownError')
      };
    }
  }

  private async getSignedUrl(filePath: string, bucket: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, SIGNED_URL_TTL.FRONTEND);

      if (error || !data?.signedUrl) return null;
      return data.signedUrl;
    } catch (error) {
      console.error('❌ WhatsAppMediaService: Failed to get signed URL:', error);
      return null;
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  extractMediaIdFromMessage(message: { id: string; media_id?: string; metadata?: unknown; [key: string]: unknown }): string | null {
    console.log('🔍 WhatsAppMediaService: extractMediaIdFromMessage called:', {
      messageId: message.id,
      hasDirectMediaId: !!message.media_id,
      directMediaIdValue: message.media_id,
      hasMetadata: !!message.metadata,
      metadataType: typeof message.metadata,
      metadataValue: message.metadata
    });

    if (message.media_id) {
      console.log('✅ WhatsAppMediaService: Found direct media_id:', message.media_id);
      return message.media_id;
    }

    if (message.metadata) {
      let parsedMetadata = message.metadata;

      if (typeof message.metadata === 'string') {
        try {
          parsedMetadata = JSON.parse(message.metadata);
          console.log('✅ WhatsAppMediaService: Parsed metadata from string:', parsedMetadata);
        } catch (error) {
          console.error('❌ WhatsAppMediaService: Failed to parse metadata JSON:', {
            error,
            metadataString: message.metadata
          });
          return null;
        }
      }

      const mediaId = parsedMetadata.media_id ||
             parsedMetadata.media?.id ||
             parsedMetadata.whatsapp_media_id ||
             null;

      if (mediaId) {
        console.log('✅ WhatsAppMediaService: Extracted media_id from metadata:', mediaId);
      } else {
        console.warn('⚠️ WhatsAppMediaService: No media_id found in metadata structure:', {
          parsedMetadata,
          checkedPaths: ['media_id', 'media.id', 'whatsapp_media_id']
        });
      }

      return mediaId;
    }

    console.error('❌ WhatsAppMediaService: No metadata available for extraction');
    return null;
  }

  clearCache(): void {
    this.cache.clear();
    this.signedCache.clear();
    this.downloadInProgress.clear();
  }

  isDevelopmentMode(): boolean {
    return this.developmentMode;
  }

  getCacheStatus() {
    return {
      cacheSize: this.cache.size,
      signedCacheSize: this.signedCache.size,
      pendingDownloads: this.downloadInProgress.size,
      cacheEntries: Array.from(this.cache.keys())
    };
  }

  validateCacheEntry(key: string): boolean {
    const url = this.cache.get(key);
    if (!url) return false;

    try {
      new URL(url);
      return true;
    } catch {
      this.cache.delete(key);
      return false;
    }
  }
}

export const whatsappMediaService = WhatsAppMediaService.getInstance();
